import Vector from "victor";
import { shapeStrokeWidthScale } from "../util";
import { PenlikeShape2 } from "./PenShape";

function getMidpoint(p1: [number, number], p2: [number, number]): number[] {
  return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
}

/**
 * Can render PenShape objects to DOM.
 */
export class PenShapeRenderer {
  id: string;
  shape: PenlikeShape2;
  numDrawnPoints = 0;

  constructor(shape: PenlikeShape2, id: string) {
    this.id = id;
    this.shape = shape;
  }

  get strokeWidth(): number {
    return this.shape.strokeWidth * shapeStrokeWidthScale(this.shape);
  }

  get points(): Array<[number, number]> {
    return this.shape.points;
  }

  update(newShape: PenlikeShape2) {
    this.shape = newShape;
  }

  /// Set ctx.strokeStyle before calling this
  drawShape(ctx: CanvasRenderingContext2D) {
    // align to pixel grid
    const offset =
      this.strokeWidth % 2 == 0 ? new Vector(0, 0) : new Vector(0.5, 0.5);
    ctx.translate(offset.x, offset.y);

    if (this.shape.isOpen) {
      this.drawIncremental(ctx);
    } else {
      this.drawAll(ctx);
    }
  }

  private drawDot(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath();
    ctx.arc(
      this.points[0][0],
      this.points[0][1],
      this.strokeWidth / 2,
      0,
      Math.PI * 2
    );
    ctx.fill();
  }

  private beginDrawing(ctx: CanvasRenderingContext2D) {
    ctx.beginPath();
    ctx.moveTo(this.points[0][0], this.points[0][1]);
  }

  // Draws the line when there are only 2 points in the whole shape
  private drawTwoPointLine(ctx: CanvasRenderingContext2D) {
    const p = this.points[1];
    ctx.lineTo(p[0], p[1]);
    ctx.stroke();
  }

  private drawInitialLine(ctx: CanvasRenderingContext2D) {
    const firstMidpoint = getMidpoint(this.points[0], this.points[1]);
    ctx.lineTo(firstMidpoint[0], firstMidpoint[1]);
  }

  private drawQuadraticCurveEndingAt(i: number, ctx: CanvasRenderingContext2D) {
    const b = this.points[i - 1];
    const c = this.points[i];
    const midpointBC = getMidpoint(b, c);
    ctx.quadraticCurveTo(b[0], b[1], midpointBC[0], midpointBC[1]);
  }

  private drawIncremental(ctx: CanvasRenderingContext2D) {
    if (this.points.length <= 0) return;

    if (this.points.length === 1) {
      this.numDrawnPoints = 1;
      return; // don't bother drawing the dot, no one will notice
    }

    if (this.points.length === 2) {
      this.numDrawnPoints = 2;
      return; // don't bother drawing the two-point line line, no one will notice
    }

    this.beginDrawing(ctx);

    this.numDrawnPoints = Math.max(this.numDrawnPoints, 2);

    while (this.numDrawnPoints < this.points.length) {
      if (this.numDrawnPoints < 3) {
        // draw line to midpoint between first 2 points
        this.drawInitialLine(ctx);
      } else {
        // if we've already drawn the initial line, we need to move the path
        // cursor to the end of the last place we drew up to
        const lastMidpoint = getMidpoint(
          this.points[this.numDrawnPoints - 3],
          this.points[this.numDrawnPoints - 2]
        );
        ctx.moveTo(lastMidpoint[0], lastMidpoint[1]);
      }
      for (let i = this.numDrawnPoints - 1; i < this.points.length - 1; i++) {
        this.drawQuadraticCurveEndingAt(i, ctx);
      }

      ctx.stroke();

      this.numDrawnPoints += 1;
    }

    // Don't bother drawing the last half of the last line, no one will notice
  }

  private drawAll(ctx: CanvasRenderingContext2D) {
    if (this.points.length <= 0) return;

    if (this.points.length === 1) {
      this.drawDot(ctx);
      return;
    }

    this.beginDrawing(ctx);

    if (this.points.length == 2) {
      this.drawTwoPointLine(ctx);
      return;
    }

    this.drawInitialLine(ctx);

    for (let i = 2; i < this.points.length - 1; i++) {
      this.drawQuadraticCurveEndingAt(i, ctx);
    }

    const lastPoint = this.points[this.points.length - 1];
    ctx.lineTo(lastPoint[0], lastPoint[1]);

    ctx.stroke();
  }
}
